import { useState } from "react";
import { LogRuns, TicketLogEntry } from "../../../interfaces/processing";

import useProcessingLogs from "../../../hooks/processing/useProcessingLogs";
import { 
  Table, 
  TableSortLabel, 
  TableCell, 
  Paper,
  TableHead, 
  TableRow, 
  TableBody, 
  Typography, 
  CircularProgress,
  TableContainer
} from "@mui/material";
import SearchByTicketId from "../../../components/processing/SearchByTicketId";
import { LoadingButton } from "@mui/lab";
import CustomerTicketRow from "../../../components/processing/customer/CustomerTicketRow";
import ErrorMessage from "../../../components/shared/ErrorMessage";
import { Add } from "@mui/icons-material";


export default function TicketsList() {
  const { 
    loadError, 
    loadMoreLogs, 
    getLogByTicketId, 
    accountLogsRuns, 
    logsLoading,
    nextPageLoading,
    hasMorePages
  } = useProcessingLogs(true);
  const [orderBy, setOrderBy] = useState<keyof TicketLogEntry>('created_at');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');

  const handleRequestSort = (property: keyof TicketLogEntry) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedLogs: LogRuns[] = accountLogsRuns.slice().sort((a, b) => {
    const aValue = a.ticket_log[orderBy] ?? '';
    const bValue = b.ticket_log[orderBy] ?? '';
    if (aValue < bValue) return order === 'asc' ? -1 : 1;
    if (aValue > bValue) return order === 'asc' ? 1 : -1;
    return 0;
  });

  return (
    <div>
      <SearchByTicketId getLogByTicketId={getLogByTicketId} renderComponent="CustomerTicketRow" />

      {loadError && <ErrorMessage errorMsg={loadError} />}

      {!logsLoading && <TableContainer component={Paper} sx={{ mt: 3 }}>
        <Table sx={{ minWidth: 650 }} aria-label="Customer ticket list">
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'external_id'}
                  direction={orderBy === 'external_id' ? order : 'asc'}
                  onClick={() => handleRequestSort('external_id')}
                >
                  Ticket NR
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'created_at'}
                  direction={orderBy === 'created_at' ? order : 'asc'}
                  onClick={() => handleRequestSort('created_at')}
                >
                  Created Date
                </TableSortLabel>
              </TableCell>
              <TableCell>Processing runs</TableCell>
              <TableCell>Processed</TableCell>
              <TableCell>Final status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedLogs.map((log_runs) => {
              const log = log_runs.ticket_log;
              const runs = log_runs.runs;
              return (
                <CustomerTicketRow key={log.id} ticketLogEntry={log} runs={runs} />
              )
            })}

            {sortedLogs.length === 0 && (
              <TableRow sx={{ height: '100px' }}>
                <TableCell colSpan={4} align="center">
                  <Typography variant="body1" color="textSecondary">
                    No tickets found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>}

      {logsLoading && <div style={{marginTop: '100px', display: 'flex', justifyContent: 'center'}}><CircularProgress /></div>}

      {hasMorePages && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            marginTop: '100px'
          }}
        >
          <LoadingButton 
            loading={nextPageLoading}
            onClick={loadMoreLogs} 
            startIcon={<Add />} 
            variant="contained"
          >
            Load more tickets
          </LoadingButton>
        </div>
      )}
    </div>
  )
}